/** Website catalog module settings (tiles layouts, chrome, filters). */

import type { CSSProperties } from 'react';
import {
  normalizeNoteColorRole,
  noteColorRoleCssVar,
  type NoteColorRole,
} from './noteBlog';
import {
  normalizeAppearanceFontWeight,
  normalizeAppearancePercentScale,
  STOREFRONT_MENU_FONT_SCALE_MAX,
  STOREFRONT_MENU_FONT_SCALE_MIN,
  type StorefrontAlign,
} from './storefrontAppearance';

export const CATALOG_SITE_LAYOUTS = [
  'grid5',
  'grid4',
  'grid3',
  'fence',
  'categoryCloud',
  'tagCloud',
] as const;

export type CatalogSiteLayout = (typeof CATALOG_SITE_LAYOUTS)[number];

export const CATALOG_SITE_TILE_SOURCES = [
  'categories',
  'all_subcategories',
] as const;

export type CatalogSiteTileSource = (typeof CATALOG_SITE_TILE_SOURCES)[number];

export const CATALOG_SITE_CATEGORY_CLOUD_MODES = [
  'images',
  'labels',
  'imagesAndLabels',
] as const;

export type CatalogSiteCategoryCloudMode =
  (typeof CATALOG_SITE_CATEGORY_CLOUD_MODES)[number];

export const CATALOG_SITE_TAG_CLOUD_MODES = [
  'labels',
  'labelsAndCounts',
] as const;

export type CatalogSiteTagCloudMode = (typeof CATALOG_SITE_TAG_CLOUD_MODES)[number];

export const CATALOG_SITE_TEXT_ALIGNS = ['left', 'center', 'right'] as const;

export type CatalogSiteTextAlign = (typeof CATALOG_SITE_TEXT_ALIGNS)[number];

/** Square crop, preserve upload aspect, or circular crop. */
export const CATALOG_SITE_TILE_SHAPES = ['square', 'asIs', 'circle'] as const;

export type CatalogSiteTileShape = (typeof CATALOG_SITE_TILE_SHAPES)[number];

/** Floating product card: how desktop height is derived (dialog shell only). */
export const CATALOG_SITE_PRODUCT_OVERLAY_HEIGHT_MODES = ['frame', 'photo'] as const;

export type CatalogSiteProductOverlayHeightMode =
  (typeof CATALOG_SITE_PRODUCT_OVERLAY_HEIGHT_MODES)[number];

/**
 * Where the product card lives:
 * - `dialog` — floating overlay over the site (backdrop)
 * - `immersive` — in-scene “product page” between header and footer
 */
export const CATALOG_SITE_PRODUCT_CARD_SHELLS = ['dialog', 'immersive'] as const;

export type CatalogSiteProductCardShell =
  (typeof CATALOG_SITE_PRODUCT_CARD_SHELLS)[number];

/**
 * Photo vs copy arrangement (desktop; mobile always stacks).
 * - `side` — columns (photo | text)
 * - `stack` — photo above text
 */
export const CATALOG_SITE_PRODUCT_CARD_STACKS = ['side', 'stack'] as const;

export type CatalogSiteProductCardStack =
  (typeof CATALOG_SITE_PRODUCT_CARD_STACKS)[number];

/** How variant (and add-on) axes are picked on the product card. */
export const PRODUCT_CARD_VARIANT_CONTROLS = ['chips', 'select'] as const;

export type ProductCardVariantControl =
  (typeof PRODUCT_CARD_VARIANT_CONTROLS)[number];

/**
 * Where qty + cart + favorites sit:
 * - `inline` — inside the text column (honours field order)
 * - `below` — full-width bar under photo+text (classic CoW)
 */
export const PRODUCT_CARD_CART_DOCKS = ['inline', 'below'] as const;

export type ProductCardCartDock = (typeof PRODUCT_CARD_CART_DOCKS)[number];

/** Ordered / toggleable text blocks on the product card (website + app modal). */
export const PRODUCT_CARD_BLOCK_IDS = [
  'title',
  'description',
  'variants',
  'addons',
  'commerce',
  'attributes',
  'optionSets',
  'documents',
  'cartActions',
] as const;

export type ProductCardBlockId = (typeof PRODUCT_CARD_BLOCK_IDS)[number];

export const PRODUCT_CARD_DIVIDER_STYLES = ['line', 'gap'] as const;

export type ProductCardDividerStyle = (typeof PRODUCT_CARD_DIVIDER_STYLES)[number];

/** Content block (toggle visibility) or movable horizontal divider. */
export type ProductCardLayoutItem =
  | { kind: 'block'; id: ProductCardBlockId; visible: boolean }
  | { kind: 'divider'; uid: string; style: ProductCardDividerStyle };

/** @deprecated Use ProductCardLayoutItem — kept as alias for call sites. */
export type ProductCardBlock = ProductCardLayoutItem;

export const DEFAULT_PRODUCT_CARD_BLOCKS: ProductCardLayoutItem[] = PRODUCT_CARD_BLOCK_IDS.map((id) => ({
  kind: 'block',
  id,
  visible: true,
}));

function newDividerUid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `div_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function isProductCardBlockId(raw: string): raw is ProductCardBlockId {
  return (PRODUCT_CARD_BLOCK_IDS as readonly string[]).includes(raw);
}

function isDividerStyle(raw: string): raw is ProductCardDividerStyle {
  return (PRODUCT_CARD_DIVIDER_STYLES as readonly string[]).includes(raw);
}

/**
 * Merge saved layout with known content blocks; keep dividers in relative order.
 * Accepts legacy `{ id, visible }` rows (no `kind`).
 */
export function normalizeProductCardBlocks(raw: unknown): ProductCardLayoutItem[] {
  /** @type {ProductCardLayoutItem[]} */
  const ordered: ProductCardLayoutItem[] = [];
  const seenBlocks = new Set<ProductCardBlockId>();

  if (Array.isArray(raw)) {
    for (const entry of raw) {
      if (!entry || typeof entry !== 'object') continue;
      const row = entry as Record<string, unknown>;
      const kindRaw = String(row.kind || '').trim();

      if (kindRaw === 'divider' || String(row.id || '').trim() === 'divider') {
        const styleRaw = String(row.style || row.dividerStyle || 'line').trim();
        const style: ProductCardDividerStyle = isDividerStyle(styleRaw) ? styleRaw : 'line';
        const uid = String(row.uid || row.key || '').trim() || newDividerUid();
        ordered.push({ kind: 'divider', uid, style });
        continue;
      }

      const id = String(row.id || '').trim();
      if (!isProductCardBlockId(id) || seenBlocks.has(id)) continue;
      seenBlocks.add(id);
      const visible = row.visible === false || row.visible === 0 || row.visible === 'false'
        ? false
        : true;
      ordered.push({ kind: 'block', id, visible });
    }
  }

  for (const id of PRODUCT_CARD_BLOCK_IDS) {
    if (!seenBlocks.has(id)) {
      ordered.push({ kind: 'block', id, visible: true });
    }
  }
  return ordered;
}

export function reorderProductCardItem(
  items: ProductCardLayoutItem[],
  index: number,
  delta: -1 | 1,
): ProductCardLayoutItem[] {
  const list = normalizeProductCardBlocks(items);
  const nextIndex = index + delta;
  if (index < 0 || index >= list.length || nextIndex < 0 || nextIndex >= list.length) {
    return list;
  }
  const next = [...list];
  const [item] = next.splice(index, 1);
  next.splice(nextIndex, 0, item);
  return next;
}

/** @deprecated Prefer reorderProductCardItem by index (supports dividers). */
export function reorderProductCardBlock(
  blocks: ProductCardLayoutItem[],
  id: ProductCardBlockId,
  delta: -1 | 1,
): ProductCardLayoutItem[] {
  const list = normalizeProductCardBlocks(blocks);
  const index = list.findIndex((b) => b.kind === 'block' && b.id === id);
  if (index < 0) return list;
  return reorderProductCardItem(list, index, delta);
}

export function setProductCardBlockVisible(
  blocks: ProductCardLayoutItem[],
  id: ProductCardBlockId,
  visible: boolean,
): ProductCardLayoutItem[] {
  return normalizeProductCardBlocks(blocks).map((item) =>
    (item.kind === 'block' && item.id === id ? { ...item, visible } : item),
  );
}

export function addProductCardDivider(
  items: ProductCardLayoutItem[],
  style: ProductCardDividerStyle,
  afterIndex: number | null = null,
): ProductCardLayoutItem[] {
  const list = normalizeProductCardBlocks(items);
  const divider: ProductCardLayoutItem = {
    kind: 'divider',
    uid: newDividerUid(),
    style: isDividerStyle(style) ? style : 'line',
  };
  if (afterIndex == null || afterIndex < 0 || afterIndex >= list.length) {
    return [...list, divider];
  }
  const next = [...list];
  next.splice(afterIndex + 1, 0, divider);
  return next;
}

export function removeProductCardDivider(
  items: ProductCardLayoutItem[],
  uid: string,
): ProductCardLayoutItem[] {
  const key = String(uid || '').trim();
  if (!key) return normalizeProductCardBlocks(items);
  return normalizeProductCardBlocks(items).filter(
    (item) => !(item.kind === 'divider' && item.uid === key),
  );
}

export function setProductCardDividerStyle(
  items: ProductCardLayoutItem[],
  uid: string,
  style: ProductCardDividerStyle,
): ProductCardLayoutItem[] {
  const key = String(uid || '').trim();
  const nextStyle = isDividerStyle(style) ? style : 'line';
  return normalizeProductCardBlocks(items).map((item) =>
    (item.kind === 'divider' && item.uid === key ? { ...item, style: nextStyle } : item),
  );
}

/** What fills the “more products” rail under the product card. */
export const CATALOG_SITE_PRODUCT_MORE_SOURCES = [
  'similar',
  'popular',
  'manual',
  'promo',
] as const;

export type CatalogSiteProductMoreSource =
  (typeof CATALOG_SITE_PRODUCT_MORE_SOURCES)[number];

/**
 * How the rail is shown (immersive only).
 * Dialog shell always uses a bottom sheet — strip would steal card space.
 * Legacy `both` normalizes to `sheet`.
 */
export const CATALOG_SITE_PRODUCT_MORE_PRESENTATIONS = [
  'sheet',
  'strip',
] as const;

export type CatalogSiteProductMorePresentation =
  (typeof CATALOG_SITE_PRODUCT_MORE_PRESENTATIONS)[number];

/** How the sheet opens: triangle tap, or after a delay. */
export const CATALOG_SITE_PRODUCT_MORE_OPEN_MODES = [
  'button',
  'auto_delay',
] as const;

export type CatalogSiteProductMoreOpenMode =
  (typeof CATALOG_SITE_PRODUCT_MORE_OPEN_MODES)[number];

export type CatalogSiteProductMoreTitleI18n = {
  ru: string;
  en: string;
  he: string;
};

const EMPTY_PRODUCT_MORE_I18N: CatalogSiteProductMoreTitleI18n = {
  ru: '',
  en: '',
  he: '',
};

/** Fence accordion: grow by pushing neighbors, or by squeezing them. */
export const CATALOG_SITE_FENCE_EXPAND_MODES = ['push', 'squeeze'] as const;

export type CatalogSiteFenceExpandMode =
  (typeof CATALOG_SITE_FENCE_EXPAND_MODES)[number];

export type StorefrontCatalogSiteSettings = {
  /** Tile layer height as % of viewport (40–100). */
  viewportHeightPercent: number;
  /** Gap between category tiles in px (0–48). */
  tileGapPx: number;
  /** Gap between product cards in the website grid (0–48). */
  productGapPx: number;
  layout: CatalogSiteLayout;
  tileSource: CatalogSiteTileSource;
  categoryCloudMode: CatalogSiteCategoryCloudMode;
  tagCloudMode: CatalogSiteTagCloudMode;
  /**
   * Show variant tag cloud under category layouts (grid/fence/categoryCloud).
   * Layout `tagCloud` is tags-only and always shows the cloud.
   */
  showTagCloud: boolean;
  /** Show named option-set chips in the website catalog and menu search. */
  showOptionSetFilters: boolean;
  /** Dictionary keys available as storefront filter groups. */
  optionSetFilterKeys: string[];
  /** Show attached named option-set values in the product panel. */
  showOptionSetsOnProductCard: boolean;
  /**
   * Colorful hashed accents for variant + option-set filter chips.
   * When false, chips use zone mono / accent selected styles.
   */
  filterChipsColorful: boolean;
  /**
   * Hide variant tags with productCount below this threshold (0–50).
   * 0 = show all tags.
   */
  tagCloudMinCount: number;
  /** Base font size for tag buttons as % of the weight-scaled rem size (50–200). */
  tagCloudFontScale: number;
  /** Font-weight for tag button labels (100–900). */
  tagCloudFontWeight: number;
  /** How an open fence plank grows (`push` = shift right; `squeeze` = shrink others). */
  fenceExpandMode: CatalogSiteFenceExpandMode;
  bgRole: NoteColorRole;
  textRole: NoteColorRole;
  /** Padding on all sides (px) for the catalog zone. */
  insetPx: number;
  textAlign: CatalogSiteTextAlign;
  tileShape: CatalogSiteTileShape;
  /** Border width on category tiles (0–10). */
  tileBorderWidthPx: number;
  tileBorderRole: NoteColorRole;
  tileShadowRole: NoteColorRole;
  /** Soft drop-shadow strength (0–10). */
  tileShadowStrength: number;
  /**
   * Caption lift along the tile square: 0 = below, 50 = center, 100 = above.
   */
  tileCaptionLiftPercent: number;
  /** Inner padding (px) between tile square edge and the image. */
  tileImageInsetPx: number;
  /** Caption font size as % of 0.875rem base (100–250). */
  tileCaptionFontScale: number;
  /** Caption font-weight (100–900). */
  tileCaptionFontWeight: number;
  /**
   * In-catalog product title size as % of caption/body text (100–300).
   * Default 200 ≈ twice the body.
   */
  productTitleFontScale: number;
  /** Product title font-weight (100–900). */
  productTitleFontWeight: number;
  /** Blank lines after the product title before params (0–3). */
  productTitleGapLines: number;
  /** Draw a border around the floating product overlay. */
  productOverlayShowBorder: boolean;
  /** Border width of the floating product overlay (px). */
  productOverlayBorderWidthPx: number;
  /** Theme color role for the floating product overlay border. */
  productOverlayBorderRole: NoteColorRole;
  /** Max width of the floating product overlay (px). */
  productOverlayMaxWidthPx: number;
  /** Outer gap from viewport edges / under sticky header (px). */
  productOverlayMarginPx: number;
  /**
   * Desktop card height (dialog shell):
   * - `frame` — fill the overlay slot; photo object-fits into its column.
   * - `photo` — card height follows photo aspect; photo width = photoShare.
   */
  productOverlayHeightMode: CatalogSiteProductOverlayHeightMode;
  /** Dialog overlay vs in-scene product page (between site header and footer). */
  productCardShell: CatalogSiteProductCardShell;
  /** Photo|text columns vs photo above text (desktop; phone always stacks). */
  productCardStack: CatalogSiteProductCardStack;
  /** Share of the card width (side) or emphasis for photo (20–80). */
  productPhotoSharePercent: number;
  /** Website product card: chips vs selects for variants/add-ons. */
  productCardVariantControlWebsite: ProductCardVariantControl;
  /** /app catalog modal: chips vs selects for variants/add-ons. */
  productCardVariantControlApp: ProductCardVariantControl;
  /** Qty/cart/favorites: in text column vs bar under the whole card. */
  productCardCartDock: ProductCardCartDock;
  /** Product card text blocks: order + visibility (saved with UI presets). */
  productCardBlocks: ProductCardLayoutItem[];
  /** “More products” rail (similar / popular / manual / promo). */
  productMoreEnabled: boolean;
  productMoreSource: CatalogSiteProductMoreSource;
  /** Immersive only; dialog forces sheet. */
  productMorePresentation: CatalogSiteProductMorePresentation;
  productMoreOpenMode: CatalogSiteProductMoreOpenMode;
  /** Used when openMode is auto_delay. */
  productMoreAutoDelaySec: number;
  productMoreLimit: number;
  /** Empty → default i18n by source. */
  productMoreTitleI18n: CatalogSiteProductMoreTitleI18n;
  /** Manual source: product UUIDs (picker UI later). */
  productMoreProductIds: string[];
  /** Promo source fields (empty → stub copy on storefront). */
  productMorePromoImageUrl: string;
  productMorePromoBodyI18n: CatalogSiteProductMoreTitleI18n;
  productMorePromoHref: string;
};

export const DEFAULT_STOREFRONT_CATALOG_SITE: StorefrontCatalogSiteSettings = {
  viewportHeightPercent: 70,
  tileGapPx: 12,
  productGapPx: 12,
  layout: 'grid4',
  tileSource: 'categories',
  categoryCloudMode: 'imagesAndLabels',
  tagCloudMode: 'labelsAndCounts',
  showTagCloud: false,
  // Option-set filters default off; tenant enables in Settings → website catalog.
  showOptionSetFilters: false,
  optionSetFilterKeys: [],
  showOptionSetsOnProductCard: false,
  filterChipsColorful: true,
  tagCloudMinCount: 0,
  tagCloudFontScale: 85,
  tagCloudFontWeight: 600,
  fenceExpandMode: 'push',
  bgRole: 'pageBg',
  textRole: 'textPrimary',
  insetPx: 0,
  textAlign: 'left',
  tileShape: 'square',
  tileBorderWidthPx: 1,
  tileBorderRole: 'borderColor',
  tileShadowRole: 'textPrimary',
  tileShadowStrength: 2,
  tileCaptionLiftPercent: 0,
  tileImageInsetPx: 0,
  tileCaptionFontScale: 100,
  tileCaptionFontWeight: 500,
  productTitleFontScale: 200,
  productTitleFontWeight: 700,
  productTitleGapLines: 1,
  productOverlayShowBorder: true,
  productOverlayBorderWidthPx: 1,
  productOverlayBorderRole: 'borderColor',
  productOverlayMaxWidthPx: 832,
  productOverlayMarginPx: 12,
  productOverlayHeightMode: 'frame',
  productCardShell: 'dialog',
  productCardStack: 'side',
  productPhotoSharePercent: 50,
  productCardVariantControlWebsite: 'chips',
  productCardVariantControlApp: 'select',
  productCardCartDock: 'below',
  productCardBlocks: DEFAULT_PRODUCT_CARD_BLOCKS.map((b) => ({ ...b })),
  productMoreEnabled: false,
  productMoreSource: 'similar',
  productMorePresentation: 'sheet',
  productMoreOpenMode: 'button',
  productMoreAutoDelaySec: 8,
  productMoreLimit: 8,
  productMoreTitleI18n: { ...EMPTY_PRODUCT_MORE_I18N },
  productMoreProductIds: [],
  productMorePromoImageUrl: '',
  productMorePromoBodyI18n: { ...EMPTY_PRODUCT_MORE_I18N },
  productMorePromoHref: '',
};

function clampInt(raw: unknown, min: number, max: number, fallback: number): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function pickEnum<T extends string>(
  raw: unknown,
  allowed: readonly T[],
  fallback: T,
): T {
  const key = String(raw || '').trim();
  return (allowed as readonly string[]).includes(key) ? (key as T) : fallback;
}

function normalizeProductMoreI18n(raw: unknown): CatalogSiteProductMoreTitleI18n {
  if (!raw || typeof raw !== 'object') {
    return { ...EMPTY_PRODUCT_MORE_I18N };
  }
  const source = raw as Record<string, unknown>;
  return {
    ru: String(source.ru ?? '').trim(),
    en: String(source.en ?? '').trim(),
    he: String(source.he ?? '').trim(),
  };
}

function normalizeProductMoreProductIds(raw: unknown): string[] {
  const list = Array.isArray(raw)
    ? raw
    : typeof raw === 'string'
      ? raw.split(/[\s,;]+/)
      : [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of list) {
    const id = String(item || '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= 48) break;
  }
  return out;
}

function readBool(raw: unknown, fallback: boolean): boolean {
  if (typeof raw === 'boolean') return raw;
  if (raw === 1 || raw === '1' || raw === 'true') return true;
  if (raw === 0 || raw === '0' || raw === 'false') return false;
  return fallback;
}

export function normalizeStorefrontCatalogSite(raw: unknown): StorefrontCatalogSiteSettings {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_STOREFRONT_CATALOG_SITE };
  }
  const source = raw as Record<string, unknown>;
  const textAlignRaw = source.textAlign ?? source.text_align ?? source.align;
  return {
    viewportHeightPercent: clampInt(
      source.viewportHeightPercent ?? source.viewport_height_percent,
      40,
      100,
      DEFAULT_STOREFRONT_CATALOG_SITE.viewportHeightPercent,
    ),
    tileGapPx: clampInt(
      source.tileGapPx ?? source.tile_gap_px,
      0,
      48,
      DEFAULT_STOREFRONT_CATALOG_SITE.tileGapPx,
    ),
    productGapPx: clampInt(
      source.productGapPx ?? source.product_gap_px,
      0,
      48,
      DEFAULT_STOREFRONT_CATALOG_SITE.productGapPx,
    ),
    layout: pickEnum(
      source.layout,
      CATALOG_SITE_LAYOUTS,
      DEFAULT_STOREFRONT_CATALOG_SITE.layout,
    ),
    tileSource: pickEnum(
      source.tileSource ?? source.tile_source,
      CATALOG_SITE_TILE_SOURCES,
      DEFAULT_STOREFRONT_CATALOG_SITE.tileSource,
    ),
    categoryCloudMode: pickEnum(
      source.categoryCloudMode ?? source.category_cloud_mode,
      CATALOG_SITE_CATEGORY_CLOUD_MODES,
      DEFAULT_STOREFRONT_CATALOG_SITE.categoryCloudMode,
    ),
    tagCloudMode: pickEnum(
      source.tagCloudMode ?? source.tag_cloud_mode,
      CATALOG_SITE_TAG_CLOUD_MODES,
      DEFAULT_STOREFRONT_CATALOG_SITE.tagCloudMode,
    ),
    showTagCloud: (() => {
      const raw = source.showTagCloud ?? source.show_tag_cloud;
      if (typeof raw === 'boolean') return raw;
      if (raw === 1 || raw === '1' || raw === 'true') return true;
      if (raw === 0 || raw === '0' || raw === 'false') return false;
      return DEFAULT_STOREFRONT_CATALOG_SITE.showTagCloud;
    })(),
    showOptionSetFilters: (() => {
      const raw = source.showOptionSetFilters ?? source.show_option_set_filters;
      if (typeof raw === 'boolean') return raw;
      if (raw === 1 || raw === '1' || raw === 'true') return true;
      if (raw === 0 || raw === '0' || raw === 'false') return false;
      return DEFAULT_STOREFRONT_CATALOG_SITE.showOptionSetFilters;
    })(),
    optionSetFilterKeys: (() => {
      const raw = source.optionSetFilterKeys ?? source.option_set_filter_keys;
      if (!Array.isArray(raw)) return [...DEFAULT_STOREFRONT_CATALOG_SITE.optionSetFilterKeys];
      return [...new Set(raw
        .map((key) => String(key || '').trim().toLowerCase())
        .filter((key) => /^[a-z][a-z0-9_]{0,62}$/.test(key)))];
    })(),
    showOptionSetsOnProductCard: (() => {
      const raw = source.showOptionSetsOnProductCard ?? source.show_option_sets_on_product_card;
      if (typeof raw === 'boolean') return raw;
      if (raw === 1 || raw === '1' || raw === 'true') return true;
      if (raw === 0 || raw === '0' || raw === 'false') return false;
      return DEFAULT_STOREFRONT_CATALOG_SITE.showOptionSetsOnProductCard;
    })(),
    filterChipsColorful: (() => {
      const raw = source.filterChipsColorful ?? source.filter_chips_colorful;
      if (typeof raw === 'boolean') return raw;
      if (raw === 1 || raw === '1' || raw === 'true') return true;
      if (raw === 0 || raw === '0' || raw === 'false') return false;
      return DEFAULT_STOREFRONT_CATALOG_SITE.filterChipsColorful;
    })(),
    tagCloudMinCount: clampInt(
      source.tagCloudMinCount ?? source.tag_cloud_min_count,
      0,
      50,
      DEFAULT_STOREFRONT_CATALOG_SITE.tagCloudMinCount,
    ),
    tagCloudFontScale: clampInt(
      source.tagCloudFontScale ?? source.tag_cloud_font_scale,
      50,
      200,
      DEFAULT_STOREFRONT_CATALOG_SITE.tagCloudFontScale,
    ),
    tagCloudFontWeight: normalizeAppearanceFontWeight(
      source.tagCloudFontWeight ?? source.tag_cloud_font_weight,
      DEFAULT_STOREFRONT_CATALOG_SITE.tagCloudFontWeight,
    ),
    fenceExpandMode: pickEnum(
      source.fenceExpandMode ?? source.fence_expand_mode,
      CATALOG_SITE_FENCE_EXPAND_MODES,
      DEFAULT_STOREFRONT_CATALOG_SITE.fenceExpandMode,
    ),
    bgRole: normalizeNoteColorRole(
      source.bgRole ?? source.bg_role,
      DEFAULT_STOREFRONT_CATALOG_SITE.bgRole,
    ),
    textRole: normalizeNoteColorRole(
      source.textRole ?? source.text_role,
      DEFAULT_STOREFRONT_CATALOG_SITE.textRole,
    ),
    insetPx: clampInt(
      source.insetPx ?? source.inset_px,
      0,
      64,
      DEFAULT_STOREFRONT_CATALOG_SITE.insetPx,
    ),
    textAlign: pickEnum(
      textAlignRaw,
      CATALOG_SITE_TEXT_ALIGNS,
      DEFAULT_STOREFRONT_CATALOG_SITE.textAlign,
    ),
    tileShape: pickEnum(
      source.tileShape ?? source.tile_shape,
      CATALOG_SITE_TILE_SHAPES,
      DEFAULT_STOREFRONT_CATALOG_SITE.tileShape,
    ),
    tileBorderWidthPx: clampInt(
      source.tileBorderWidthPx ?? source.tile_border_width_px,
      0,
      10,
      DEFAULT_STOREFRONT_CATALOG_SITE.tileBorderWidthPx,
    ),
    tileBorderRole: normalizeNoteColorRole(
      source.tileBorderRole ?? source.tile_border_role,
      DEFAULT_STOREFRONT_CATALOG_SITE.tileBorderRole,
    ),
    tileShadowRole: normalizeNoteColorRole(
      source.tileShadowRole ?? source.tile_shadow_role,
      DEFAULT_STOREFRONT_CATALOG_SITE.tileShadowRole,
    ),
    tileShadowStrength: clampInt(
      source.tileShadowStrength ?? source.tile_shadow_strength,
      0,
      10,
      DEFAULT_STOREFRONT_CATALOG_SITE.tileShadowStrength,
    ),
    tileCaptionLiftPercent: (() => {
      const legacyMode = String(
        source.tileCaptionMode ?? source.tile_caption_mode ?? '',
      ).trim();
      // Legacy «below» meant caption under the tile — map to lift 0.
      if (legacyMode === 'below') return 0;
      return clampInt(
        source.tileCaptionLiftPercent ?? source.tile_caption_lift_percent,
        0,
        100,
        DEFAULT_STOREFRONT_CATALOG_SITE.tileCaptionLiftPercent,
      );
    })(),
    tileImageInsetPx: clampInt(
      source.tileImageInsetPx ?? source.tile_image_inset_px,
      0,
      32,
      DEFAULT_STOREFRONT_CATALOG_SITE.tileImageInsetPx,
    ),
    tileCaptionFontScale: normalizeAppearancePercentScale(
      source.tileCaptionFontScale ?? source.tile_caption_font_scale,
      DEFAULT_STOREFRONT_CATALOG_SITE.tileCaptionFontScale,
      STOREFRONT_MENU_FONT_SCALE_MIN,
      STOREFRONT_MENU_FONT_SCALE_MAX,
    ),
    tileCaptionFontWeight: normalizeAppearanceFontWeight(
      source.tileCaptionFontWeight ?? source.tile_caption_font_weight,
      DEFAULT_STOREFRONT_CATALOG_SITE.tileCaptionFontWeight,
    ),
    productTitleFontScale: clampInt(
      source.productTitleFontScale ?? source.product_title_font_scale,
      100,
      300,
      DEFAULT_STOREFRONT_CATALOG_SITE.productTitleFontScale,
    ),
    productTitleFontWeight: normalizeAppearanceFontWeight(
      source.productTitleFontWeight ?? source.product_title_font_weight,
      DEFAULT_STOREFRONT_CATALOG_SITE.productTitleFontWeight,
    ),
    productTitleGapLines: clampInt(
      source.productTitleGapLines ?? source.product_title_gap_lines,
      0,
      3,
      DEFAULT_STOREFRONT_CATALOG_SITE.productTitleGapLines,
    ),
    productOverlayShowBorder:
      source.productOverlayShowBorder === false
      || source.product_overlay_show_border === false
        ? false
        : source.productOverlayShowBorder === true
          || source.product_overlay_show_border === true
          || DEFAULT_STOREFRONT_CATALOG_SITE.productOverlayShowBorder,
    productOverlayBorderWidthPx: clampInt(
      source.productOverlayBorderWidthPx ?? source.product_overlay_border_width_px,
      0,
      10,
      DEFAULT_STOREFRONT_CATALOG_SITE.productOverlayBorderWidthPx,
    ),
    productOverlayBorderRole: normalizeNoteColorRole(
      source.productOverlayBorderRole ?? source.product_overlay_border_role,
      DEFAULT_STOREFRONT_CATALOG_SITE.productOverlayBorderRole,
    ),
    productOverlayMaxWidthPx: clampInt(
      source.productOverlayMaxWidthPx ?? source.product_overlay_max_width_px,
      480,
      960,
      DEFAULT_STOREFRONT_CATALOG_SITE.productOverlayMaxWidthPx,
    ),
    productOverlayMarginPx: clampInt(
      source.productOverlayMarginPx ?? source.product_overlay_margin_px,
      0,
      80,
      DEFAULT_STOREFRONT_CATALOG_SITE.productOverlayMarginPx,
    ),
    productOverlayHeightMode: pickEnum(
      source.productOverlayHeightMode ?? source.product_overlay_height_mode,
      CATALOG_SITE_PRODUCT_OVERLAY_HEIGHT_MODES,
      DEFAULT_STOREFRONT_CATALOG_SITE.productOverlayHeightMode,
    ),
    productCardShell: pickEnum(
      source.productCardShell ?? source.product_card_shell,
      CATALOG_SITE_PRODUCT_CARD_SHELLS,
      DEFAULT_STOREFRONT_CATALOG_SITE.productCardShell,
    ),
    productCardStack: pickEnum(
      source.productCardStack ?? source.product_card_stack,
      CATALOG_SITE_PRODUCT_CARD_STACKS,
      DEFAULT_STOREFRONT_CATALOG_SITE.productCardStack,
    ),
    productPhotoSharePercent: clampInt(
      source.productPhotoSharePercent ?? source.product_photo_share_percent,
      20,
      80,
      DEFAULT_STOREFRONT_CATALOG_SITE.productPhotoSharePercent,
    ),
    productCardVariantControlWebsite: pickEnum(
      source.productCardVariantControlWebsite
        ?? source.product_card_variant_control_website,
      PRODUCT_CARD_VARIANT_CONTROLS,
      DEFAULT_STOREFRONT_CATALOG_SITE.productCardVariantControlWebsite,
    ),
    productCardVariantControlApp: pickEnum(
      source.productCardVariantControlApp
        ?? source.product_card_variant_control_app,
      PRODUCT_CARD_VARIANT_CONTROLS,
      DEFAULT_STOREFRONT_CATALOG_SITE.productCardVariantControlApp,
    ),
    productCardCartDock: pickEnum(
      source.productCardCartDock ?? source.product_card_cart_dock,
      PRODUCT_CARD_CART_DOCKS,
      DEFAULT_STOREFRONT_CATALOG_SITE.productCardCartDock,
    ),
    productCardBlocks: normalizeProductCardBlocks(
      source.productCardBlocks ?? source.product_card_blocks,
    ),
    productMoreEnabled: readBool(
      source.productMoreEnabled ?? source.product_more_enabled,
      DEFAULT_STOREFRONT_CATALOG_SITE.productMoreEnabled,
    ),
    productMoreSource: pickEnum(
      source.productMoreSource ?? source.product_more_source,
      CATALOG_SITE_PRODUCT_MORE_SOURCES,
      DEFAULT_STOREFRONT_CATALOG_SITE.productMoreSource,
    ),
    productMorePresentation: (() => {
      const raw = source.productMorePresentation ?? source.product_more_presentation;
      // Legacy `both` → sheet (strip+sheet combo removed).
      if (String(raw || '').trim() === 'both') {
        return 'sheet';
      }
      return pickEnum(
        raw,
        CATALOG_SITE_PRODUCT_MORE_PRESENTATIONS,
        DEFAULT_STOREFRONT_CATALOG_SITE.productMorePresentation,
      );
    })(),
    productMoreOpenMode: (() => {
      const raw = source.productMoreOpenMode ?? source.product_more_open_mode;
      // Legacy scroll-open removed — fall back to triangle.
      if (String(raw || '').trim() === 'auto_scroll') {
        return 'button';
      }
      return pickEnum(
        raw,
        CATALOG_SITE_PRODUCT_MORE_OPEN_MODES,
        DEFAULT_STOREFRONT_CATALOG_SITE.productMoreOpenMode,
      );
    })(),
    productMoreAutoDelaySec: clampInt(
      source.productMoreAutoDelaySec ?? source.product_more_auto_delay_sec,
      3,
      60,
      DEFAULT_STOREFRONT_CATALOG_SITE.productMoreAutoDelaySec,
    ),
    productMoreLimit: clampInt(
      source.productMoreLimit ?? source.product_more_limit,
      4,
      24,
      DEFAULT_STOREFRONT_CATALOG_SITE.productMoreLimit,
    ),
    productMoreTitleI18n: normalizeProductMoreI18n(
      source.productMoreTitleI18n ?? source.product_more_title_i18n,
    ),
    productMoreProductIds: normalizeProductMoreProductIds(
      source.productMoreProductIds ?? source.product_more_product_ids,
    ),
    productMorePromoImageUrl: String(
      source.productMorePromoImageUrl ?? source.product_more_promo_image_url ?? '',
    ).trim(),
    productMorePromoBodyI18n: normalizeProductMoreI18n(
      source.productMorePromoBodyI18n ?? source.product_more_promo_body_i18n,
    ),
    productMorePromoHref: String(
      source.productMorePromoHref ?? source.product_more_promo_href ?? '',
    ).trim(),
  };
}

/** Catalog-site keys owned by Settings → Product card (Theme & templates). */
export const PRODUCT_CARD_SETTING_KEYS = [
  'productGapPx',
  'productTitleFontScale',
  'productTitleFontWeight',
  'productTitleGapLines',
  'productCardShell',
  'productCardStack',
  'productCardBlocks',
  'productCardVariantControlWebsite',
  'productCardVariantControlApp',
  'productCardCartDock',
  'productPhotoSharePercent',
  'productMoreEnabled',
  'productMoreSource',
  'productMorePresentation',
  'productMoreOpenMode',
  'productMoreAutoDelaySec',
  'productMoreLimit',
  'productMoreTitleI18n',
  'productMoreProductIds',
  'productMorePromoImageUrl',
  'productMorePromoBodyI18n',
  'productMorePromoHref',
  'productOverlayHeightMode',
  'productOverlayShowBorder',
  'productOverlayBorderWidthPx',
  'productOverlayBorderRole',
  'productOverlayMaxWidthPx',
  'productOverlayMarginPx',
] as const;

export type ProductCardSettingKey = (typeof PRODUCT_CARD_SETTING_KEYS)[number];

export function pickProductCardSettings(
  settings: StorefrontCatalogSiteSettings,
): Pick<StorefrontCatalogSiteSettings, ProductCardSettingKey> {
  const out = {} as Pick<StorefrontCatalogSiteSettings, ProductCardSettingKey>;
  for (const key of PRODUCT_CARD_SETTING_KEYS) {
    const value = settings[key];
    // Skip undefined so spread onto baseline cannot wipe persisted keys.
    if (value !== undefined) {
      Object.assign(out, { [key]: value });
    }
  }
  return out;
}

export function omitProductCardSettings(
  settings: StorefrontCatalogSiteSettings,
): Omit<StorefrontCatalogSiteSettings, ProductCardSettingKey> {
  const next = { ...settings } as Record<string, unknown>;
  for (const key of PRODUCT_CARD_SETTING_KEYS) {
    delete next[key];
  }
  return next as Omit<StorefrontCatalogSiteSettings, ProductCardSettingKey>;
}

/**
 * Dialog never shares card height with a strip — sheet only.
 * Immersive: `sheet` (overlay curtain) or `strip` (under the card).
 */
export function resolveProductMoreSurfaces(
  settings: StorefrontCatalogSiteSettings,
): { showSheet: boolean; showStrip: boolean } {
  if (!settings.productMoreEnabled) {
    return { showSheet: false, showStrip: false };
  }
  if (settings.productCardShell === 'dialog') {
    return { showSheet: true, showStrip: false };
  }
  const mode = settings.productMorePresentation;
  return {
    showSheet: mode === 'sheet',
    showStrip: mode === 'strip',
  };
}

/** Map catalog-site text align onto storefront align used by product grids. */
export function catalogSiteAlignToStorefront(
  align: CatalogSiteTextAlign,
): StorefrontAlign {
  return align === 'center' ? 'center' : 'left';
}

/**
 * Caption anchor relative to the tile square:
 * 0 = below, 50 = center, 100 = above (gapPx clears the square edge).
 */
export function catalogCaptionAnchorStyle(
  liftPercent: number,
  gapPx = 8,
): CSSProperties {
  const lift = Math.min(100, Math.max(0, liftPercent));
  if (lift <= 50) {
    const u = lift / 50;
    return {
      top: `calc(${100 - 50 * u}% + ${(1 - u) * gapPx}px)`,
      transform: 'translateY(-50%)',
    };
  }
  const u = (lift - 50) / 50;
  return {
    top: `calc(${50 - 50 * u}% - ${u * gapPx}px)`,
    transform: 'translateY(-50%)',
  };
}

/** Reference tile width (px) at which caption scale 100% ≈ 0.875rem. */
export const CATALOG_CAPTION_REF_WIDTH_PX = 160;

/**
 * Caption typography. When scaleWithTile is true (default), font may shrink
 * with tile width via cqw, but never grows past the slider base size.
 *
 * Note: use `100cqw / Npx` (length/length → ratio). Never multiply two lengths.
 */
export function catalogCaptionFontStyle(settings: {
  tileCaptionFontScale: number;
  tileCaptionFontWeight: number;
  scaleWithTile?: boolean;
}): CSSProperties {
  const scale = settings.tileCaptionFontScale / 100;
  const weight = settings.tileCaptionFontWeight;
  const base = `calc(var(--sf-font-size-base, 1rem) * 0.875 * ${scale})`;
  if (settings.scaleWithTile === false) {
    return {
      fontSize: base,
      fontWeight: weight,
    };
  }
  // Shrink-only vs tile width; floor for tiny tiles.
  const shrunk = `calc(var(--sf-font-size-base, 1rem) * 0.875 * ${scale} * 100cqw / ${CATALOG_CAPTION_REF_WIDTH_PX}px)`;
  return {
    fontSize: `max(calc(var(--sf-font-size-base, 1rem) * 0.625 * ${scale}), min(${base}, ${shrunk}))`,
    fontWeight: weight,
  };
}

/** Tag cloud / chip type — % of tenant base (independent of tile captions). */
export function catalogTagCloudFontStyle(settings: {
  tagCloudFontScale: number;
  tagCloudFontWeight: number;
}): CSSProperties {
  const scale = settings.tagCloudFontScale / 100;
  return {
    fontSize: `calc(var(--sf-font-size-base, 1rem) * ${scale})`,
    fontWeight: settings.tagCloudFontWeight,
  };
}

/** Reserve vertical space when caption sits outside the square. */
export function catalogCaptionReserveStyle(
  liftPercent: number,
  labelApproxPx = 28,
  gapPx = 8,
): CSSProperties {
  const lift = Math.min(100, Math.max(0, liftPercent));
  const room = labelApproxPx + gapPx;
  if (lift <= 50) {
    const outside = 1 - lift / 50;
    return { paddingBottom: Math.round(outside * room) };
  }
  const outside = (lift - 50) / 50;
  return { paddingTop: Math.round(outside * room) };
}

/** Soft-shadow metrics shared by chrome paint and layout gap. */
export function catalogTileShadowMetrics(strength: number): {
  oy: number;
  blur: number;
  spread: number;
  /** Approximate reach past the border box on the heaviest side. */
  extentPx: number;
} {
  const s = Math.max(0, strength);
  if (s <= 0) {
    return { oy: 0, blur: 0, spread: 0, extentPx: 0 };
  }
  const oy = Math.max(1, Math.round(s * 0.4));
  const blur = Math.round(s * 1.25);
  const spread = Math.round(s * 0.1);
  return { oy, blur, spread, extentPx: oy + blur + spread };
}

/**
 * CSS gap so `tileGapPx` is air between outer chrome footprints
 * (tile box + border + shadow), not between bare boxes.
 * Grids/clouds use this; fence boards use raw `tileGapPx` so gap=0 means flush planks.
 */
export function catalogTileLayoutGapPx(settings: {
  tileGapPx: number;
  tileBorderWidthPx: number;
  tileShadowStrength: number;
}): number {
  const border = Math.max(0, settings.tileBorderWidthPx);
  const { extentPx } = catalogTileShadowMetrics(settings.tileShadowStrength);
  return Math.max(0, settings.tileGapPx + 2 * border + 2 * extentPx);
}

/** Desktop fence: minimum plank width when few categories (centered strip). */
export const CATALOG_FENCE_MIN_BOARD_WIDTH_PX = 144;

/**
 * Folded plank cap. High enough that 2–3 categories fill a reasonable share of
 * the row; hover still grows the active plank to a square (fence height).
 */
export const CATALOG_FENCE_MAX_BOARD_WIDTH_PX = 400;

/** Desktop fence: wait this long on hover before expanding the plank to a square. */
export const CATALOG_FENCE_HOVER_EXPAND_MS = 1000;

/** In `squeeze` mode, hide captions when a closed plank is narrower than this. */
export const CATALOG_FENCE_LABEL_MIN_WIDTH_PX = 88;

/** Desktop fence: at least 3× the old ~11rem strip. */
export const CATALOG_FENCE_MIN_HEIGHT_PX = 528;

/** Mobile fence accordion: collapsed row (preview + name). ~2× the old 56px strip. */
export const CATALOG_ACCORDION_CLOSED_HEIGHT_PX = 112;

/** Border + soft drop shadow from site color roles (professional deformation). */
export function catalogTileChromeStyle(settings: {
  tileBorderWidthPx: number;
  tileBorderRole: NoteColorRole;
  tileShadowRole: NoteColorRole;
  tileShadowStrength: number;
}): CSSProperties {
  const borderW = settings.tileBorderWidthPx;
  const strength = settings.tileShadowStrength;
  const shadowColor = noteColorRoleCssVar(settings.tileShadowRole);
  const alpha = Math.min(55, 16 + strength * 4);
  const { oy, blur, spread } = catalogTileShadowMetrics(strength);

  return {
    borderWidth: borderW,
    borderStyle: borderW > 0 ? 'solid' : 'none',
    borderColor: noteColorRoleCssVar(settings.tileBorderRole),
    boxShadow: strength <= 0
      ? 'none'
      : `0 ${oy}px ${blur}px ${spread}px color-mix(in srgb, ${shadowColor} ${alpha}%, transparent)`,
  };
}
